import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
    Box, Stack, Select, MenuItem, FormControl, InputLabel, TextField,
    IconButton, Button, ToggleButtonGroup, ToggleButton, Alert,
    Autocomplete, Typography, FormHelperText, Avatar, ListItemIcon, ListItemText,
    FormControlLabel, Checkbox,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ViewListIcon from '@mui/icons-material/ViewList';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CodeIcon from '@mui/icons-material/Code';
import TokenIcon from '@mui/icons-material/Token';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import {Coins} from 'lucide-react';
import {JsonEditor as JsonEditReact} from 'json-edit-react';
import apiService from '../services/apiService';

const ALL_ACTIONS = [
    {value: 'decrease', label: 'Decrease'},
    {value: 'increase', label: 'Increase'},
];

const ENTITY_TYPES = [
    {value: 'gelt', label: 'Gelt', icon: <TokenIcon fontSize="small"/>},
    {value: 'currency', label: 'Currency', icon: <Coins size={18}/>},
    {value: 'reward', label: 'Reward', icon: <CardGiftcardIcon fontSize="small"/>},
];

const PRESETS = {
    entryCost: {defaultAction: 'decrease', lockAction: true},
    reward: {defaultAction: 'increase', lockAction: true},
    custom: {defaultAction: 'decrease', lockAction: false},
};

const isRangeRow = (item) => {
    const min = item?.criteria?.amount_min;
    const max = item?.criteria?.amount_max;
    return (min !== undefined && min !== null) || (max !== undefined && max !== null);
};

const validateRow = (item, allowRange = false) => {
    const errors = {};
    if (!item.action) errors.action = 'Required';
    if (!item.entity) errors.entity = 'Required';
    if (!item.criteria?.identifier) errors.identifier = 'Select an item';

    if (allowRange && isRangeRow(item)) {
        const min = item.criteria?.amount_min;
        const max = item.criteria?.amount_max;
        if (min === undefined || min === null || min === '') errors.amountMin = 'Required';
        else if (typeof min === 'number' && min <= 0) errors.amountMin = 'Must be > 0';
        if (max === undefined || max === null || max === '') errors.amountMax = 'Required';
        else if (typeof max === 'number' && max <= 0) errors.amountMax = 'Must be > 0';
        if (typeof min === 'number' && typeof max === 'number' && !errors.amountMin && !errors.amountMax && min >= max) {
            errors.amountMax = 'Max must be > Min';
        }
        if (item.criteria?.amount !== undefined && item.criteria?.amount !== null) {
            errors.amount = 'Remove fixed amount when using a range';
        }
    } else {
        const amount = item.criteria?.amount;
        if (amount === undefined || amount === null || amount === '') {
            errors.amount = 'Required';
        } else if (typeof amount === 'number' && amount <= 0) {
            errors.amount = 'Must be > 0';
        }
    }
    return errors;
};

const canSwitchToVisual = (value) => {
    return Array.isArray(value) && (value.length === 0 || value.every(item =>
        item?.action && item?.entity && item?.criteria?.identifier !== undefined
    ));
};

function EntityCriteriaEditor({value = [], onChange, disabled = false, preset = 'custom', showErrors = false, allowRange = false}) {
    const {defaultAction, lockAction} = PRESETS[preset] || PRESETS.custom;
    const actions = lockAction
        ? ALL_ACTIONS.filter(a => a.value === defaultAction)
        : ALL_ACTIONS;
    const [mode, setMode] = useState('visual');

    // Per-row cache of the toggled-off side. When the user unchecks Range we drop
    // amount_min/amount_max from the saved data; this cache remembers them so a
    // re-check restores the original range instead of collapsing to min==max.
    // Keyed by row index — fine for typical add/remove/toggle interleaving.
    const rowFormCache = useRef({});
    const [textValue, setTextValue] = useState(() => JSON.stringify(value, null, 2));
    const [textError, setTextError] = useState(null);
    const [entityData, setEntityData] = useState({gelt: [], currency: [], reward: []});
    const [loadingData, setLoadingData] = useState(true);
    const [switchError, setSwitchError] = useState(null);
    const [fetchError, setFetchError] = useState(null);

    useEffect(() => {
        const failed = [];
        const safeFetch = (url, label) => apiService.get(url).catch(() => {
            failed.push(label);
            return [];
        });
        Promise.all([
            safeFetch('/api/gelts', 'gelts'),
            safeFetch('/api/currencies', 'currencies'),
            safeFetch('/api/rewards', 'rewards'),
        ]).then(([gelts, currencies, rewards]) => {
            setEntityData({gelt: gelts, currency: currencies, reward: rewards});
            // Distinguish "genuinely empty list" from "failed to load" — otherwise an item
            // picker that silently renders no options (and blanks an existing selection whose
            // id is still intact in state) looks like the referenced entity was deleted.
            setFetchError(failed.length ? `Failed to load: ${failed.join(', ')}` : null);
        }).finally(() => setLoadingData(false));
    }, []);

    const handleModeChange = useCallback((_, newMode) => {
        if (!newMode) return;
        setSwitchError(null);

        if (mode === 'text') {
            try {
                const parsed = JSON.parse(textValue);
                if (newMode === 'visual' && !canSwitchToVisual(parsed)) {
                    setSwitchError('Cannot switch to Visual: items must have action, entity, and criteria.identifier');
                    return;
                }
                onChange(parsed);
                setTextError(null);
            } catch {
                setSwitchError('Fix JSON errors before switching');
                return;
            }
        } else if (newMode === 'visual' && !canSwitchToVisual(value)) {
            setSwitchError('Cannot switch to Visual: items must have action, entity, and criteria.identifier');
            return;
        }

        if (newMode === 'text') {
            setTextValue(JSON.stringify(value, null, 2));
            setTextError(null);
        }
        setMode(newMode);
    }, [value, textValue, mode, onChange]);

    const handleTextChange = useCallback((e) => {
        const raw = e.target.value;
        setTextValue(raw);
        try {
            const parsed = JSON.parse(raw);
            setTextError(null);
            onChange(parsed);
        } catch {
            setTextError('Invalid JSON');
        }
    }, [onChange]);

    const updateRow = (index, field, val) => {
        const updated = [...value];
        if (field === 'action' || field === 'entity') {
            updated[index] = {...updated[index], [field]: val};
            if (field === 'entity') {
                updated[index].criteria = {...updated[index].criteria, identifier: ''};
            }
        } else if (field === 'identifier') {
            updated[index] = {...updated[index], criteria: {...updated[index].criteria, identifier: val}};
        } else if (field === 'amount' || field === 'amount_min' || field === 'amount_max') {
            updated[index] = {...updated[index], criteria: {...updated[index].criteria, [field]: val}};
        }
        onChange(updated);
    };

    const setRowType = (index, type) => {
        if (!type) return;
        const updated = [...value];
        const c = {...updated[index].criteria};

        // Snapshot the row's current values into the cache so the side we are
        // about to drop survives the toggle and can be restored later.
        const cached = rowFormCache.current[index] || {};
        rowFormCache.current[index] = {
            amount: c.amount ?? cached.amount,
            amount_min: c.amount_min ?? cached.amount_min,
            amount_max: c.amount_max ?? cached.amount_max,
        };
        const remembered = rowFormCache.current[index];

        if (type === 'range') {
            delete c.amount;
            c.amount_min = remembered.amount_min ?? remembered.amount ?? 1;
            c.amount_max = remembered.amount_max ?? remembered.amount ?? 1;
        } else {
            delete c.amount_min;
            delete c.amount_max;
            c.amount = remembered.amount ?? remembered.amount_min ?? 1;
        }
        updated[index] = {...updated[index], criteria: c};
        onChange(updated);
    };

    const addRow = () => onChange([...value, {action: defaultAction, entity: 'gelt', criteria: {identifier: '', amount: 1}}]);

    const removeRow = (index) => {
        // Shift cache entries left to keep them aligned with the new indices
        const next = {};
        Object.keys(rowFormCache.current).forEach(k => {
            const i = Number(k);
            if (i < index) next[i] = rowFormCache.current[i];
            else if (i > index) next[i - 1] = rowFormCache.current[i];
        });
        rowFormCache.current = next;
        onChange(value.filter((_, i) => i !== index));
    };

    const getItemOptions = (entityType) => {
        return (entityData[entityType] || []).map(item => ({
            identifier: String(item.id),
            label: item.name || item.identifier || String(item.id),
            iconUrl: item.iconUrl || null,
        }));
    };

    const renderVisual = () => (
        <Stack spacing={1.5}>
            {value.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{py: 1}}>
                    No entries. Click "Add Entry" to begin.
                </Typography>
            )}
            {value.map((item, index) => {
                const options = getItemOptions(item.entity);
                const itemId = String(item.criteria?.identifier ?? '');
                const selectedOption = options.find(o => o.identifier === itemId) || null;
                const errors = showErrors ? validateRow(item, allowRange) : {};
                const rowIsRange = allowRange && isRangeRow(item);

                return (
                    <Box key={index} sx={{
                        p: 1.5,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.25,
                    }}>
                    {/* Top row: action + entity + item, with trash on the far right */}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start"
                           sx={{flexWrap: 'wrap', rowGap: 1}}>
                        <FormControl size="small" sx={{minWidth: 120, flexShrink: 0}} error={!!errors.action}>
                            <InputLabel>Action</InputLabel>
                            <Select
                                value={item.action || defaultAction}
                                label="Action"
                                onChange={e => updateRow(index, 'action', e.target.value)}
                                disabled={disabled || lockAction}
                            >
                                {actions.map(a => <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>)}
                            </Select>
                            {errors.action && <FormHelperText>{errors.action}</FormHelperText>}
                        </FormControl>

                        <FormControl size="small" sx={{minWidth: 140, flexShrink: 0}} error={!!errors.entity}>
                            <InputLabel>Entity</InputLabel>
                            <Select
                                value={item.entity || ''}
                                label="Entity"
                                onChange={e => updateRow(index, 'entity', e.target.value)}
                                disabled={disabled}
                                renderValue={val => {
                                    const et = ENTITY_TYPES.find(t => t.value === val);
                                    return et ? (
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                            {et.icon}<span>{et.label}</span>
                                        </Stack>
                                    ) : val;
                                }}
                            >
                                {ENTITY_TYPES.map(e => (
                                    <MenuItem key={e.value} value={e.value}>
                                        <ListItemIcon sx={{minWidth: 28}}>{e.icon}</ListItemIcon>
                                        <ListItemText>{e.label}</ListItemText>
                                    </MenuItem>
                                ))}
                            </Select>
                            {errors.entity && <FormHelperText>{errors.entity}</FormHelperText>}
                        </FormControl>

                        <Autocomplete
                            size="small"
                            sx={{minWidth: 160, flex: 1}}
                            options={options}
                            getOptionLabel={o => o.label}
                            value={selectedOption}
                            onChange={(_, newVal) => updateRow(index, 'identifier', newVal?.identifier || '')}
                            isOptionEqualToValue={(option, val) => option.identifier === val?.identifier}
                            disabled={disabled}
                            renderOption={(props, option) => (
                                <li {...props} key={option.identifier}>
                                    {option.iconUrl && (
                                        <Avatar src={option.iconUrl} sx={{width: 20, height: 20, mr: 1}}/>
                                    )}
                                    {option.label}
                                </li>
                            )}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    label="Item"
                                    error={!!errors.identifier}
                                    helperText={errors.identifier}
                                    slotProps={{
                                        input: {
                                            ...params.InputProps,
                                            startAdornment: selectedOption?.iconUrl ? (
                                                <>
                                                    <Avatar src={selectedOption.iconUrl}
                                                            sx={{width: 20, height: 20, mr: 0.5}}/>
                                                    {params.InputProps.startAdornment}
                                                </>
                                            ) : params.InputProps.startAdornment,
                                        },
                                    }}
                                />
                            )}
                        />

                        <IconButton
                            color="error"
                            onClick={() => removeRow(index)}
                            disabled={disabled}
                            size="small"
                            sx={{mt: '4px', flexShrink: 0}}
                            aria-label="Remove entry"
                        >
                            <DeleteIcon/>
                        </IconButton>
                    </Stack>

                    {/* Bottom row: range checkbox + amount-or-min/max */}
                    <Stack direction="row" spacing={1.5} alignItems="flex-start">
                        {allowRange && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={rowIsRange}
                                        onChange={(_, v) => setRowType(index, v ? 'range' : 'fixed')}
                                        disabled={disabled}
                                    />
                                }
                                label="Range"
                                sx={{
                                    m: 0,
                                    mt: '4px',
                                    flexShrink: 0,
                                    '& .MuiFormControlLabel-label': {fontSize: '0.85rem'},
                                }}
                            />
                        )}

                        {rowIsRange ? (
                            <>
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Min"
                                    sx={{width: 120, flexShrink: 0}}
                                    value={item.criteria?.amount_min ?? ''}
                                    onChange={e => updateRow(index, 'amount_min', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    disabled={disabled}
                                    slotProps={{htmlInput: {min: 1}}}
                                    error={!!errors.amountMin}
                                    helperText={errors.amountMin}
                                />
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Max"
                                    sx={{width: 120, flexShrink: 0}}
                                    value={item.criteria?.amount_max ?? ''}
                                    onChange={e => updateRow(index, 'amount_max', e.target.value === '' ? '' : parseInt(e.target.value))}
                                    disabled={disabled}
                                    slotProps={{htmlInput: {min: 1}}}
                                    error={!!errors.amountMax}
                                    helperText={errors.amountMax}
                                />
                            </>
                        ) : (
                            <TextField
                                size="small"
                                type="number"
                                label="Amount"
                                sx={{width: 140, flexShrink: 0}}
                                value={item.criteria?.amount ?? ''}
                                onChange={e => updateRow(index, 'amount', e.target.value === '' ? '' : parseInt(e.target.value))}
                                disabled={disabled}
                                slotProps={{htmlInput: {min: 1}}}
                                error={!!errors.amount}
                                helperText={errors.amount}
                            />
                        )}
                    </Stack>
                    </Box>
                );
            })}
            <Box>
                <Button startIcon={<AddIcon/>} onClick={addRow} disabled={disabled} size="small">
                    Add Entry
                </Button>
            </Box>
        </Stack>
    );

    return (
        <Box>
            <Stack direction="row" spacing={2} alignItems="center" sx={{mb: 2}}>
                <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small">
                    <ToggleButton value="visual"><ViewListIcon sx={{mr: 0.5}}/> Visual</ToggleButton>
                    <ToggleButton value="tree"><AccountTreeIcon sx={{mr: 0.5}}/> Tree</ToggleButton>
                    <ToggleButton value="text"><CodeIcon sx={{mr: 0.5}}/> Text</ToggleButton>
                </ToggleButtonGroup>
                {loadingData && <Typography variant="caption" color="text.secondary">Loading items...</Typography>}
            </Stack>

            {fetchError && <Alert severity="warning" sx={{mb: 1.5}}>{fetchError}</Alert>}
            {switchError && <Alert severity="warning" sx={{mb: 1.5}}>{switchError}</Alert>}

            {mode === 'visual' && renderVisual()}

            {mode === 'tree' && (
                <Box sx={{'& .jer-editor-container': {fontFamily: 'monospace', fontSize: '0.875rem'}}}>
                    <JsonEditReact
                        data={value}
                        setData={onChange}
                        rootName="entryCost"
                        collapse={2}
                        restrictEdit={disabled}
                        restrictDelete={disabled}
                        restrictAdd={disabled}
                        restrictTypeSelection={disabled}
                        minWidth="100%"
                    />
                </Box>
            )}

            {mode === 'text' && (
                <>
                    {textError && <Alert severity="error" sx={{mb: 1}}>{textError}</Alert>}
                    <TextField
                        fullWidth
                        multiline
                        minRows={4}
                        maxRows={20}
                        value={textValue}
                        onChange={handleTextChange}
                        disabled={disabled}
                        error={!!textError}
                        placeholder='[{"action":"decrease","entity":"gelt","criteria":{"identifier":"1","amount":1}}]'
                        slotProps={{input: {sx: {fontFamily: 'monospace', fontSize: '0.875rem'}}}}
                    />
                </>
            )}
        </Box>
    );
}

EntityCriteriaEditor.validate = (value, allowRange = false) => {
    if (!Array.isArray(value)) return 'Must be an array';
    for (let i = 0; i < value.length; i++) {
        const errors = validateRow(value[i], allowRange);
        if (Object.keys(errors).length > 0) {
            return `Row ${i + 1}: ${Object.values(errors).join(', ')}`;
        }
    }
    return null;
};

export default EntityCriteriaEditor;
